import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type DoseStateEntry = {
  is_already_sent: boolean;
  latest_status: string;
  latest_created_at: string | null;
};

type DoseStateRow = {
  pet_application_id: string;
  due_date: string;
  latest_dispatch_id: string;
  latest_status: string;
  latest_created_at: string | null;
  is_already_sent: boolean;
  can_retry: boolean;
};

function toDueDateKey(dueDate: unknown): string {
  if (typeof dueDate === "string") return dueDate.slice(0, 10);
  return String(dueDate).slice(0, 10);
}

/**
 * Fetches unified WhatsApp reminder dose state for the given clinics.
 * Returns a map keyed by `pet_application_id:due_date` (YYYY-MM-DD) for use in report/scheduler alignment.
 */
export function useWhatsAppDoseState(clinicIds: string[]) {
  const sortedIds = useMemo(
    () => [...new Set(clinicIds)].filter(Boolean).sort(),
    [clinicIds]
  );

  return useQuery({
    queryKey: ["whatsapp-dose-state", sortedIds],
    queryFn: async (): Promise<Map<string, DoseStateEntry>> => {
      const map = new Map<string, DoseStateEntry>();
      await Promise.all(
        sortedIds.map(async (clinicId) => {
          const { data, error } = await supabase.rpc("get_whatsapp_application_reminder_dose_state", {
            _clinic_id: clinicId,
            _pet_application_id: null,
          });
          if (error) return;
          const rows = (data ?? []) as DoseStateRow[];
          for (const row of rows) {
            const key = `${row.pet_application_id}:${toDueDateKey(row.due_date)}`;
            map.set(key, {
              is_already_sent: row.is_already_sent === true,
              latest_status: row.latest_status ?? "",
              latest_created_at: row.latest_created_at ?? null,
            });
          }
        })
      );
      return map;
    },
    enabled: sortedIds.length > 0,
  });
}
